import React from 'react';
import PropTypes from 'prop-types';
import { useI18n } from 'hooks/use_i18n';
import './pagination.scss';
import { Icon } from 'components/ui';

const Pagination = ({
  pagination: { total_pages, current_page },
  current_page_gap = 2,
  onPageChange,
  currentPage,
  className,
  ariaLabel = '',
  showArrows,
  ...rest
}) => {
  const { t } = useI18n();

  if (total_pages === 1) {
    return null;
  }

  const generatePages = () => {
    const pages = [];

    if (total_pages <= 7) {
      for (let i = 1; i <= total_pages; i++) {
        pages.push(i);
      }
      return pages;
    }

    pages.push(1);

    let start = Math.max(2, current_page - current_page_gap);
    let end = Math.min(total_pages - 1, current_page + current_page_gap);

    if (current_page <= 4) {
      start = 2;
      end = 5;
    }

    if (current_page >= total_pages - 3) {
      start = total_pages - 4;
      end = total_pages - 1;
    }

    if (start > 2) {
      pages.push('...');
    }

    for (let i = start; i <= end; i++) {
      if (i > 1 && i < total_pages) {
        pages.push(i);
      }
    }

    if (end < total_pages - 1) {
      pages.push('...');
    }

    if (total_pages > 1) {
      pages.push(total_pages);
    }

    return pages;
  };

  const pages = generatePages();
  const canPreviousPage = current_page > 1;
  const canNextPage = current_page < total_pages;

  return (
    <nav aria-label={ariaLabel} className={className} {...rest}>
      <div className="pagination">
        <div
          className={`pagination-item nav previous ${!canPreviousPage ? 'disabled' : ''}`}
          onClick={(ev) => {
            ev.preventDefault();
            canPreviousPage && onPageChange(current_page - 1);
          }}
        >
          {showArrows && <Icon name="HiMiniChevronDown" className="arrow-icon" style={{ transform: 'rotate(90deg)' }} />}
          {t('components.ui.pagination.previous')}
        </div>

        {pages.map((page, index) => {
          if (page === '...') {
            return (
              <div key={`ellipsis-${index}`} className="pagination-item ellipsis">
                ...
              </div>
            );
          }

          return (
            <div
              className={`pagination-item ${page === current_page ? 'active' : ''}`}
              key={index}
              onClick={(ev) => {
                ev.preventDefault();
                onPageChange(page);
              }}
            >
              {page}
            </div>
          );
        })}

        <div
          className={`pagination-item nav next ${!canNextPage ? 'disabled' : ''}`}
          onClick={(ev) => {
            ev.preventDefault();
            canNextPage && onPageChange(current_page + 1);
          }}
        >
          {t('components.ui.pagination.next')}
          {showArrows && <Icon name="HiMiniChevronDown" className="arrow-icon" style={{ transform: 'rotate(270deg)' }} />}
        </div>
      </div>
    </nav>
  );
};

Pagination.defaultProps = {
  current_page_gap: 2, // Réduit à 2 pour limiter l'affichage
};

Pagination.propTypes = {
  pagination: PropTypes.shape({
    current_page: PropTypes.number.isRequired,
    total_pages: PropTypes.number.isRequired,
    size: PropTypes.number,
    total_count: PropTypes.number,
  }).isRequired,
  onPageChange: PropTypes.func.isRequired,
  current_page_gap: PropTypes.number,
  showArrows: PropTypes.bool,
};

export default Pagination;
